import React, { useEffect, useRef, useState } from 'react';
import './Map.css';

const TURKEY_CENTER = { lat: 38.9637, lng: 35.2433 };

function loadGoogleMaps(apiKey) {
  return new Promise((resolve, reject) => {
    if (window.google && window.google.maps) {
      resolve(window.google);
      return;
    }
    const existing = document.getElementById('google-maps-js');
    if (existing) {
      existing.addEventListener('load', () => resolve(window.google));
      existing.addEventListener('error', reject);
      return;
    }
    const script = document.createElement('script');
    script.id = 'google-maps-js';
    script.async = true;
    script.defer = true;
    script.crossOrigin = "anonymous";
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
    script.onload = () => resolve(window.google);
    script.onerror = reject;
    document.head.appendChild(script);
  });
}

function loadLeaflet() {
  return new Promise((resolve, reject) => {
    if (window.L && typeof window.L.map === 'function') {
      resolve(window.L);
      return;
    }
    const existing = document.getElementById('leaflet-js');
    const existingCss = document.getElementById('leaflet-css');
    const finish = () => (window.L ? resolve(window.L) : reject(new Error('Leaflet failed to load')));
    if (!existingCss) {
      const link = document.createElement('link');
      link.id = 'leaflet-css';
      link.rel = 'stylesheet';
      link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
      document.head.appendChild(link);
    }
    if (existing) {
      existing.addEventListener('load', finish);
      existing.addEventListener('error', reject);
      return;
    }
    const script = document.createElement('script');
    script.id = 'leaflet-js';
    script.async = true;
    script.defer = true;
    script.crossOrigin = "anonymous";
    script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
    script.onload = finish;
    script.onerror = reject;
    document.head.appendChild(script);
  });
}

const Map = ({ markers = [], selectedIndex = null, hoveredIndex = null, onMarkerClick }) => {
  const mapRef = useRef(null);
  const [engine, setEngine] = useState(null); // 'google' | 'leaflet'
  const [mapReady, setMapReady] = useState(false);
  const gmapRef = useRef(null);
  const gmarkersRef = useRef([]);
  const lmapRef = useRef(null);
  const lmarkersRef = useRef([]);
  const isMounted = useRef(false);

  // Fallback and state tracking refs to avoid stale closures in setTimeout
  const engineRef = useRef(null);
  const mapReadyRef = useRef(false);
  const fallbackTriggeredRef = useRef(false);

  useEffect(() => {
    isMounted.current = true;
    let cleanup = () => {};
    const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
    const isInvalidKey = !apiKey || apiKey.trim() === "" || apiKey.includes('LUTFEN_API_ANAHTARINIZI_BURAYA_YAPISTIRIN');

    const startLeaflet = () => {
      if (fallbackTriggeredRef.current || engineRef.current === 'leaflet') return;
      fallbackTriggeredRef.current = true;
      
      loadLeaflet()
        .then((L) => {
          if (!mapRef.current || !isMounted.current) return;
          try {
            const map = L.map(mapRef.current, {
              tap: false,
            }).setView([TURKEY_CENTER.lat, TURKEY_CENTER.lng], 5);
            L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
              attribution: '&copy; OpenStreetMap contributors',
              maxZoom: 19,
            }).addTo(map);
            lmapRef.current = map;
            engineRef.current = 'leaflet';
            setEngine('leaflet');
            mapReadyRef.current = true;
            setMapReady(true);
            
            setTimeout(() => {
              map.invalidateSize();
            }, 100);
          } catch (err) {
            console.error("Leaflet instantiation failed:", err);
          }
        })
        .catch((err) => {
          console.error("Both map engines failed:", err);
        });
    };

    window.gm_authFailure = () => {
      console.warn("Google Maps Auth Failure detected. Swapping to Leaflet.");
      startLeaflet();
    };

    const startGoogle = () => {
      const timeoutId = setTimeout(() => {
        // Use the refs to check if map is ready, which will always have the current value
        if (!mapReadyRef.current && engineRef.current !== 'leaflet') {
          console.warn("Google Maps render timeout. Swapping to Leaflet.");
          startLeaflet();
        }
      }, 4000);

      loadGoogleMaps(apiKey)
        .then((google) => {
          if (!mapRef.current || !isMounted.current || fallbackTriggeredRef.current || engineRef.current != null) return;
          try {
            const mapInstance = new google.maps.Map(mapRef.current, {
              center: TURKEY_CENTER,
              zoom: 5,
              mapTypeControl: false,
              streetViewControl: false,
              fullscreenControl: true,
            });
            gmapRef.current = mapInstance;
            engineRef.current = 'google';
            setEngine('google');
            mapReadyRef.current = true;
            setMapReady(true);
          } catch (err) {
            console.warn("Google Maps instantiation failed, falling back to Leaflet.", err);
            startLeaflet();
          }
        })
        .catch(() => {
          console.warn("Google Maps script load failed, falling back to Leaflet.");
          startLeaflet();
        });

      return () => clearTimeout(timeoutId);
    };

    if (!isInvalidKey) {
      const stopTimer = startGoogle();
      cleanup = () => {
        isMounted.current = false;
        stopTimer && stopTimer();
        delete window.gm_authFailure;
      };
    } else {
      console.warn("Valid Google Maps API Key not found. Falling back to Leaflet.");
      startLeaflet();
      cleanup = () => {
        isMounted.current = false;
      };
    }

    const baseCleanup = cleanup;
    cleanup = () => {
      baseCleanup();
      isMounted.current = false;
      
      // Clear Leaflet
      if (lmapRef.current) {
        try {
          const map = lmapRef.current;
          lmarkersRef.current.forEach(m => m && map.removeLayer(m));
          lmarkersRef.current = [];
          map.off();
          map.remove();
        } catch (e) {
          console.warn("Leaflet cleanup error:", e);
        }
        lmapRef.current = null;
      }

      // Clear Google
      if (gmapRef.current) {
        try {
          gmarkersRef.current.forEach(m => m && m.setMap && m.setMap(null));
          gmarkersRef.current = [];
        } catch (e) {}
        gmapRef.current = null;
      }
    };

    return cleanup;
  }, []);

  useEffect(() => {
    if (!mapReady || !isMounted.current) return;
    
    if (engine === 'google') {
      const map = gmapRef.current;
      if (!map || !isMounted.current) return;
      gmarkersRef.current.forEach(m => m.setMap(null));
      gmarkersRef.current = [];
      if (!markers || markers.length === 0) return;
      
      const google = window.google;
      if (!google) return;
      const bounds = new google.maps.LatLngBounds();
      markers.forEach((mk, idx) => {
        if (!mk || typeof mk.lat !== 'number' || typeof mk.lng !== 'number') return;
        if (mk.lat === 0 && mk.lng === 0) return;
        const marker = new google.maps.Marker({
          position: { lat: mk.lat, lng: mk.lng },
          map,
          title: mk.title || `Lokasyon ${idx + 1}`,
        });
        marker.__propIndex = mk.idx ?? idx;
        if (typeof onMarkerClick === 'function') {
          marker.addListener('click', () => {
            if (isMounted.current) onMarkerClick(marker.__propIndex);
          });
        }
        gmarkersRef.current.push(marker);
        bounds.extend(marker.getPosition());
      });
      if (gmarkersRef.current.length === 1) {
        map.setCenter(gmarkersRef.current[0].getPosition());
        map.setZoom(13);
      } else if (gmarkersRef.current.length > 1) {
        map.fitBounds(bounds, 60);
      }
    } else if (engine === 'leaflet') {
      const map = lmapRef.current;
      if (!map || !isMounted.current) return;
      lmarkersRef.current.forEach(m => { if (m) map.removeLayer(m); });
      lmarkersRef.current = [];
      if (!markers || markers.length === 0) return;
      
      const L = window.L;
      if (!L) return;
      const bounds = L.latLngBounds([]);
      markers.forEach((mk, idx) => {
        if (!mk || typeof mk.lat !== 'number' || typeof mk.lng !== 'number') return;
        if (mk.lat === 0 && mk.lng === 0) return;
        const marker = L.marker([mk.lat, mk.lng], { title: mk.title || `Lokasyon ${idx + 1}` });
        marker.__propIndex = mk.idx ?? idx;
        if (typeof onMarkerClick === 'function') {
          marker.on('click', () => {
            if (isMounted.current) onMarkerClick(marker.__propIndex);
          });
        }
        marker.addTo(map);
        lmarkersRef.current.push(marker);
        bounds.extend(marker.getLatLng());
      });
      if (lmarkersRef.current.length === 1) {
        map.setView(lmarkersRef.current[0].getLatLng(), 13);
      } else if (lmarkersRef.current.length > 1) {
        map.fitBounds(bounds.pad(0.1));
      }
    }
  }, [markers, mapReady, engine, onMarkerClick]);

  useEffect(() => {
    if (!mapReady || !isMounted.current) return;
    
    if (selectedIndex != null) {
      if (engine === 'google') {
        const map = gmapRef.current;
        if (!map || !isMounted.current) return;
        const marker = gmarkersRef.current.find(m => m && m.__propIndex === selectedIndex);
        if (!marker) return;

        // Add bounce animation
        if (window.google && window.google.maps) {
          marker.setAnimation(window.google.maps.Animation.BOUNCE);
          setTimeout(() => marker.setAnimation(null), 2100); // 3 bounces (~700ms each)
        }
        
        const pos = marker.getPosition();
        const isMobile = window.innerWidth <= 1024;
        const latOffset = isMobile ? 0.006 : 0; // Move it up on mobile
        
        map.panTo({ lat: pos.lat() + latOffset, lng: pos.lng() });
        map.setZoom(Math.max(map.getZoom(), 13));
      } else if (engine === 'leaflet') {
        const map = lmapRef.current;
        if (!map || !isMounted.current) return;
        const marker = lmarkersRef.current.find(m => m && m.__propIndex === selectedIndex);
        if (!marker) return;

        // Add bounce animation class
        const icon = marker.getElement();
        if (icon) {
          icon.classList.remove('marker-bounce');
          void icon.offsetWidth; // Force reflow
          icon.classList.add('marker-bounce');
          setTimeout(() => icon.classList.remove('marker-bounce'), 1300);
        }

        const ll = marker.getLatLng();
        const isMobile = window.innerWidth <= 1024;
        const latOffset = isMobile ? 0.006 : 0; // Move it up on mobile
        
        map.setView([ll.lat + latOffset, ll.lng], Math.max(map.getZoom() || 5, 13));
      }
    } else {
      if (engine === 'google') {
        const map = gmapRef.current;
        if (!map || !isMounted.current || gmarkersRef.current.length === 0) return;
        const bounds = new window.google.maps.LatLngBounds();
        gmarkersRef.current.forEach(m => bounds.extend(m.getPosition()));
        if (gmarkersRef.current.length === 1) {
          map.setCenter(gmarkersRef.current[0].getPosition());
          map.setZoom(13);
        } else {
          map.fitBounds(bounds, 60);
        }
      } else if (engine === 'leaflet') {
        const map = lmapRef.current;
        if (!map || !isMounted.current || lmarkersRef.current.length === 0) return;
        const bounds = window.L.latLngBounds(lmarkersRef.current.map(m => m.getLatLng()));
        if (lmarkersRef.current.length === 1) {
          map.setView(lmarkersRef.current[0].getLatLng(), 13);
        } else {
          map.fitBounds(bounds.pad(0.1));
        }
      }
    }
  }, [selectedIndex, mapReady, engine]);

  useEffect(() => {
    if (!mapReady) return;
    
    if (engine === 'google') {
      const markersArr = gmarkersRef.current;
      if (!markersArr || markersArr.length === 0) return;
      if (hoveredIndex == null) {
        markersArr.forEach(m => { if (m) m.setZIndex(1); });
        return;
      }
      markersArr.forEach((m) => {
        if (!m) return;
        if (m.__propIndex === hoveredIndex) m.setZIndex(1000); else m.setZIndex(1);
      });
    } else if (engine === 'leaflet') {
      const markersArr = lmarkersRef.current;
      if (!markersArr || markersArr.length === 0) return;
      if (hoveredIndex == null) {
        markersArr.forEach(m => { if (m && m.setZIndexOffset) m.setZIndexOffset(0); });
        return;
      }
      markersArr.forEach((m) => {
        if (!m || !m.setZIndexOffset) return;
        if (m.__propIndex === hoveredIndex) m.setZIndexOffset(1000); else m.setZIndexOffset(0);
      });
    }
  }, [hoveredIndex, mapReady, engine]);

  return (
    <div className="map-container">
      {!mapReady && <div className="map-error">Harita servisleri yükleniyor...</div>}
      <div ref={mapRef} className="map-canvas" />
    </div>
  );
};

export default Map;
